import { initialize, LDUser } from 'launchdarkly-js-client-sdk';
import { areUsersEqual } from './utils';

export const FEATURE_FLAG_SHARED_USER_KEY = 'anonymous';

export interface FeatureFlagServiceOptions {
    apiKey: string;
    user?: LDUser;
    flagDefaultValue?: boolean;
    flagOverrides?: Record<string, boolean>;
    configFlagOverrides?:
        | {
              key: string;
              value: string | boolean | number;
          }[]
        | null;
}

export interface SetUserOptions {
    shouldOptimizeAPICalls?: boolean;
}

export interface FeatureFlagService {
    initialize: () => Promise<void>;
    isInitialized: () => boolean;
    setUser: (user: LDUser, options?: SetUserOptions) => Promise<void>;
    hasFlag: (flagName: string) => boolean;
    getFlagValue: (flagName: string) => unknown;
}

/**
 * Creates instance of FeatureFlagService
 *
 * The service is meant to be optional and fails gracefully if not loaded properly or if an error occurs during loading (e.g. invalid api key).
 * The initialize method must be called before it's used. If the initialization fails, an error is thrown.
 *
 * @param options.apiKey - LaunchDarkly environment clientId
 * @param options.user - LaunchDarkly user options
 * @param options.flagDefaultValue - a default value returned for not found flags
 * @param options.configFlagOverrides - value overrides that are received from BE configuration that circumvent LaunchDarkly completely for specific flags.
 * @param options.flagOverrides - value overrides that circumvent LaunchDarkly completely for specific flags. They take priority over `configFlagOverrides`.
 */
export const createFeatureFlagService = ({
    apiKey,
    user = { anonymous: true, key: FEATURE_FLAG_SHARED_USER_KEY },
    flagDefaultValue = false,
    configFlagOverrides,
    flagOverrides = {},
}: FeatureFlagServiceOptions): FeatureFlagService => {
    const overrides: Record<string, boolean> = {
        ...configFlagOverrides
            /**
             * We explicitely accept only boolean flags; numeric and any other constructs are not supported on Frontend.
             */
            ?.filter(({ value }) => typeof value === 'boolean')
            .reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}),
        ...flagOverrides,
    };

    let loadedSuccessfully = false;
    let initialized = false;
    const instance = initialize(apiKey, user);
    instance.on('error', () => {
        // Without this handler errors would be logged to console
    });

    return {
        initialize: async () => {
            if (apiKey) {
                await instance.waitForInitialization();
                loadedSuccessfully = true;
            }
            initialized = true;
        },

        isInitialized: () => initialized,

        setUser: async (newUser: LDUser, { shouldOptimizeAPICalls }: SetUserOptions = {}) => {
            let sharedUser = newUser;
            if (newUser.anonymous === true) {
                sharedUser = {
                    ...newUser,
                    key: FEATURE_FLAG_SHARED_USER_KEY,
                };
            }

            if (loadedSuccessfully) {
                if (shouldOptimizeAPICalls === true && areUsersEqual(sharedUser, instance.getUser())) {
                    return;
                }

                await instance.identify(sharedUser);
            }
        },

        hasFlag: (flagName: string) => {
            if (flagName in overrides) {
                return overrides[flagName];
            }

            return loadedSuccessfully ? instance.variation(flagName, flagDefaultValue) : flagDefaultValue;
        },

        getFlagValue: (flagName: string) => {
            if (flagName in overrides) {
                return overrides[flagName];
            }

            return loadedSuccessfully ? instance.variation(flagName, flagDefaultValue) : flagDefaultValue;
        },
    };
};

interface GetActivefeatureFlagsParams {
    featureFlagService: FeatureFlagService;
    featureFlagEnum: Record<string, string>;
}

export const getActiveFeatureFlags = ({ featureFlagService, featureFlagEnum }: GetActivefeatureFlagsParams) =>
    Object.values(featureFlagEnum).filter((flag) => featureFlagService.hasFlag(flag));
